import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/ProtectedRoute';
import toast from 'react-hot-toast';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, user: null, action: null });
  const navigate = useNavigate();
  const { user: currentUser, isAdmin, isManager } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.users.list();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const { user, action } = actionDialog;
      await api.users.update(user.id, { active: action === 'activate' });
      toast.success(`User ${action === 'activate' ? 'activated' : 'deactivated'} successfully`);
      setActionDialog({ open: false, user: null, action: null });
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={
            params.value === 'admin' ? 'primary' : 
            params.value === 'manager' ? 'secondary' : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {!(isManager && !isAdmin && params.row.role === 'admin') && (
            <IconButton
              size="small"
              onClick={() => navigate(`/users/${params.row.id}/edit`)}
              title="Edit User"
            >
              <EditIcon />
            </IconButton>
          )}
          
          {params.row.id !== currentUser.id && 
           !(isManager && !isAdmin && params.row.role === 'admin') && (
            <IconButton
              size="small"
              onClick={() =>
                setActionDialog({
                  open: true,
                  user: params.row,
                  action: params.row.active ? 'deactivate' : 'activate',
                })
              }
              title={params.row.active ? 'Deactivate User' : 'Activate User'}
              color={params.row.active ? 'error' : 'success'}
            >
              {params.row.active ? <BlockIcon /> : <CheckCircleIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const getActionText = () => {
    if (!actionDialog.user) return '';
    const action = actionDialog.action;
    const userName = actionDialog.user.name;
    
    if (action === 'activate') {
      return `Are you sure you want to activate user "${userName}"? They will be able to log in and access the system.`;
    } else {
      return `Are you sure you want to deactivate user "${userName}"? They will no longer be able to log in.`;
    }
  };

  return (
    <ProtectedRoute requireManagerOrAdmin>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight={600}>
            Users Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
          >
            Add User
          </Button>
        </Box>

        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </Paper>

        {/* Action Confirmation Dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={() => setActionDialog({ open: false, user: null, action: null })}
        >
          <DialogTitle>
            {actionDialog.action === 'activate' ? 'Activate User' : 'Deactivate User'}
          </DialogTitle>
          <DialogContent>
            <Typography>{getActionText()}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialog({ open: false, user: null, action: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleToggleActive}
              color={actionDialog.action === 'activate' ? 'success' : 'error'}
              variant="contained"
            >
              {actionDialog.action === 'activate' ? 'Activate' : 'Deactivate'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProtectedRoute>
  );
};

export default Users;
